import { action, computed, makeObservable, observable, reaction, toJS } from 'mobx';
import moment from 'moment';
import { Notification } from '@bringg/react-components';
import { TaskTypeId } from '@bringg/types';
import type {
	GanttPublicApi,
	GanttTimeLineItem,
	GroupColumnDefinition,
	GroupRow,
	TimelineItemValuesHistory
} from '@bringg-frontend/gantt-timeline';
import _first from 'lodash/first';

import type RoutesPlannerStore from '../../stores/routes-planner-store';
import { assignTasksToDate } from '../../stores/helpers/tasks-by-dates';
import {
	breakToGanttItems,
	consecutiveRouteToGanttItem,
	createGanttRow,
	deliveryBlockToGanttItem,
	taskToGanttItem,
	rechargeTimeToGanttItem
} from '../../utils/bringgTypesTransformers';
import { isTaskAssigned } from '../../components/routes-planner/utils';
import { calculateChangesAfterItemMoved, getChangedRoutesData, getTaskDuration } from '../helpers/helpers';
import { NO_ROUTE_ID } from '../routes-planner-store';
import { isTaskInTimeWindowRange } from '../../components/routes-planner/timeline-logic/tasks-logic';
import { getResourceDeliveryBlocks } from '../../components/routes-planner/timeline-logic/delivery-block-logic';
import { errorNotifierWrapper } from '../../services/error-notifier';
import type { RoutesPlannerTask } from '../../types';
import { consecutiveRoutesProvider } from '../../services/consecutive-routes/consecutive-routes-provider';
import { etaRoutesConverter } from '../../services/eta/eta-routes-converter';
import { dateUtils } from '../../utils/date-utils';

export default class GanttStore {
	rootStore: RoutesPlannerStore;
	ganttApi: GanttPublicApi;

	// this state is part of the for vistimeline, solving the cluster redraw issue
	lastUpdatedRunIds: number[] = [];

	constructor(rootStore: RoutesPlannerStore) {
		makeObservable(this, {
			ganttItems: computed,
			ganttGroups: computed,
			ganttColumns: computed,
			centerMapByTask: action,
			onDrop: action,
			lastUpdatedRunIds: observable,
			setLastUpdatedRunIds: action,
			getRoutesAsGanttItemBySelectedRouteId: action
		});

		this.rootStore = rootStore;

		reaction(
			() => this.rootStore.runsToDisplay,
			(runsToDisplay, prevRunsToDisplay) => {
				if (runsToDisplay.length > prevRunsToDisplay.length) {
					const firstTaskToFocus = this.getTaskToFocusOn(runsToDisplay, prevRunsToDisplay);

					setTimeout(() => {
						firstTaskToFocus && this.focusTaskOnGantt(firstTaskToFocus);
					}, 0);
				}
			}
		);
	}

	setGanttRef = (ref: GanttPublicApi) => {
		this.ganttApi = ref;
	};

	get ganttItems(): GanttTimeLineItem[] {
		const ganttItemsFromRunId = (items: GanttTimeLineItem[], runId: number) => {
			if (Number(runId) === NO_ROUTE_ID) {
				return items;
			}

			const runTasks = this.rootStore.runToTasksInPresentedDate[runId] || [];
			const runBreaks = this.rootStore.runToBreaks[runId];
			const ganttItems: GanttTimeLineItem[] = runTasks.map(task =>
				taskToGanttItem(task, this.rootStore.routesColors.get(task.run_id))
			);

			const runInfo = this.rootStore.routeInfoByIndex[runId] || this.rootStore.routesById[runId];
			const runStartTime = dateUtils.getDateFromUnix(runInfo.depot_etl).toISOString();
			const runEndTime = dateUtils.getDateFromUnix(runInfo.depot_back_eta).toISOString();
			const isPlannedRun = this.rootStore.routesById[runId].is_planned;

			if (runBreaks?.length) {
				const breakGanttItems = breakToGanttItems(
					runBreaks,
					this.rootStore.routesColors.get(runId),
					this.rootStore.translations.breakSymbol
				);
				ganttItems.push(...breakGanttItems);
			}

			if (
				this.rootStore.shouldCalculateEtaGroupedByDriver &&
				this.rootStore.consecutiveRouteFF &&
				runTasks.length > 0
			) {
				const consecutiveRoutes = this.getRoutesAsGanttItemBySelectedRouteId(runId);
				consecutiveRoutes.forEach(consecutiveRoute => ganttItems.push(consecutiveRoute));
			}

			if (isPlannedRun) {
				const initialLoadingTime = this.rootStore.configuration.initialLoadingTimeMinutes;
				const initialUnloadingTime = this.rootStore.configuration.initialUnloadingTimeMinutes;
				if (initialLoadingTime) {
					ganttItems.push(rechargeTimeToGanttItem(runInfo, initialLoadingTime, true));
				}
				if (initialUnloadingTime) {
					ganttItems.push(rechargeTimeToGanttItem(runInfo, initialUnloadingTime, false));
				}
			}

			getResourceDeliveryBlocks(
				this.rootStore.routesById[runId],
				this.rootStore.deliveryBlocksByDriver,
				this.rootStore.deliveryBlocksByVehicle,
				runStartTime,
				runEndTime
			).forEach(deliveryBlock => {
				ganttItems.push(deliveryBlockToGanttItem(deliveryBlock, runId));
			});

			return items.concat(ganttItems);
		};

		return toJS(this.rootStore.runsToDisplay.reduce(ganttItemsFromRunId, []));
	}

	get ganttGroups(): GroupRow[] {
		const { runsToDisplay, routesById, vehiclesById, shouldShowVehicles, runToDriver } = this.rootStore;
		return toJS(
			runsToDisplay
				.filter(runId => runId !== NO_ROUTE_ID)
				.reduce((items, runId) => {
					const route = routesById[runId];
					const ganttRow = createGanttRow(
						route.id,
						route.title,
						runToDriver[runId],
						vehiclesById[routesById[runId]?.vehicle_id],
						shouldShowVehicles
					);
					return items.concat(ganttRow);
				}, [])
		);
	}

	get ganttColumns(): GroupColumnDefinition[] {
		const { translations, shouldShowVehicles } = this.rootStore;
		const columns = [{ display: translations.runsTab }, { display: translations.driversTab }];

		if (shouldShowVehicles) {
			columns.push({ display: translations.vehiclesTab });
		}

		return columns;
	}

	centerMapByTask = (items: GanttTimeLineItem[]) => {
		const clickedItem = items[0];

		const { lng, lat } = this.rootStore.runToTasksInPresentedDate[clickedItem.rowId].find(
			item => item.id === clickedItem.id
		).way_points[0];

		this.rootStore.mapStore.setMapCenter({ lng, lat });
	};

	onItemMoveEnded = async (item: GanttTimeLineItem, valuesHistory: TimelineItemValuesHistory): Promise<boolean> => {
		const {
			runToTasksInPresentedDate,
			runToBreaks,
			etaCalculator,
			executeEtaCalculationFlow,
			translations,
			updateTasks,
			clearRunDirectionsAndBreaks,
			routesById,
			routeInfoByIndex,
			runToDriver
		} = this.rootStore;

		const task = runToTasksInPresentedDate[valuesHistory.originalGroupId]?.find(task => task.id === item.id);
		let prevChangedTaskInfo: Partial<Bringg.Task> | undefined;

		if (task) {
			prevChangedTaskInfo = {
				id: task.id,
				run_id: task.run_id,
				priority: task.priority,
				linked_task_id: task.linked_task_id
			};
		}

		const tasksToUpdateByRun = calculateChangesAfterItemMoved(
			item,
			valuesHistory,
			runToTasksInPresentedDate,
			runToDriver,
			runToBreaks,
			this.ganttItems,
			translations.notificationsTranslations
		);

		if (this.rootStore.inProgressEtaCalculations.has(item.rowId)) {
			return;
		}

		if (tasksToUpdateByRun) {
			if (prevChangedTaskInfo) {
				this.rootStore.addModifiedTasks(item.rowId, [prevChangedTaskInfo], tasksToUpdateByRun);
			}

			if (etaCalculator) {
				const currentRunId = item.rowId;
				const previousRunId = valuesHistory.previousGroupId;

				const changedRoutesData = getChangedRoutesData(
					currentRunId,
					Number(previousRunId),
					tasksToUpdateByRun,
					runToBreaks,
					routesById,
					routeInfoByIndex
				);

				try {
					await errorNotifierWrapper({
						callback: async () =>
							executeEtaCalculationFlow(changedRoutesData, tasksToUpdateByRun, true, [Number(item.id)]),
						errorMessagesMap: translations.errorMessages
					});
					const emptyRoute = changedRoutesData.find(item => item.runOrderedTasks.length === 0);
					if (emptyRoute) {
						clearRunDirectionsAndBreaks(emptyRoute.runId);
					}
					this.selectTaskOnGantt([Number(item.id)]);
				} catch (e) {
					// reverse changes
					console.error(e);

					return false;
				}
			} else {
				updateTasks(tasksToUpdateByRun);
			}
		} else {
			clearRunDirectionsAndBreaks(item.rowId);
		}
		return true;
	};

	selectTaskOnGantt = (taskIds: number[]) => {
		this.ganttApi?.api.setSelection(taskIds, { focus: false, animation: {} });
	};

	focusTaskOnGantt = (taskToFocus: RoutesPlannerTask) => {
		this.setGanttScreenCenter(taskToFocus.id);
		this.setGanttToFirstTaskOnWindow();
	};

	setGanttScreenCenter = (taskId: number) => {
		this.ganttApi?.api.focus(taskId, {
			zoom: false,
			animation: false
		});
	};

	setGanttToFirstTaskOnWindow = () => {
		const currentWindow = this.ganttApi?.api.getWindow();
		this.ganttApi?.api.moveTo(currentWindow.end, {
			zoom: false,
			animation: { easingFunction: 'easeOutCubic', duration: 100 }
		});
	};

	getTaskToFocusOn = (runsIds: number[], prevRunsIds: number[]): RoutesPlannerTask | undefined => {
		const diff = runsIds.filter(id => isTaskAssigned(id) && !prevRunsIds.includes(id));

		if (diff.length) {
			return this.getEarliestTask(diff);
		}

		return undefined;
	};

	getEarliestTask = (runIds: number[]) => {
		let indexOfRunWithEarlierTask = 0;
		runIds.forEach((runId, index) => {
			const earliestTask = _first(this.rootStore.runToTasksInPresentedDate[runIds[indexOfRunWithEarlierTask]]);
			const firstTaskInRun = _first(this.rootStore.runToTasksInPresentedDate[runIds[index]]);
			if (_first(firstTaskInRun.way_points).scheduled_at < _first(earliestTask.way_points).scheduled_at) {
				indexOfRunWithEarlierTask = index;
			}
		});

		const runWithEarliestTask = this.rootStore.runToTasksInPresentedDate[runIds[indexOfRunWithEarlierTask]];
		return _first(runWithEarliestTask);
	};

	setSelectedItems = (taskIds: number[], focus = false, animation = {}) => {
		this.ganttApi.api.setSelection(taskIds, { focus, animation });
	};

	onDrop = async ({ group, time }) => {
		const { tableStore, tasksById, translations, inProgressEtaCalculations, assignedTasksToRun } = this.rootStore;

		const taskId = tableStore.taskIdOfDroppedItem;
		if (!taskId) {
			return;
		}
		tableStore.setTaskIdOfDroppedItem(null);

		const runId = group;
		const updatedEta = time;

		if (inProgressEtaCalculations.has(runId)) {
			return;
		}
		const task = tasksById.get(taskId);
		const { eta, etl, etos } = task?.way_points?.[0] || {};
		const delta = getTaskDuration(eta, etl, etos);
		const updatedEtl = new Date(updatedEta.getTime() + delta);

		if (
			!getResourceDeliveryBlocks(
				this.rootStore.routesById[runId],
				this.rootStore.deliveryBlocksByDriver,
				this.rootStore.deliveryBlocksByVehicle,
				updatedEta.toISOString(),
				updatedEtl.toISOString(),
				this.rootStore.configuration.timelineConfig.canSetTasksPastDeliveryBlock
			).length
		) {
			Notification.error(translations.notificationsTranslations.outOfDeliveryBlockRange);
			return;
		}
		if (!isTaskInTimeWindowRange(task, updatedEta, updatedEtl)) {
			return;
		}
		let tasksToAssign = [];
		if (task.linked_task_id) {
			const linkedTask = tasksById.get(task.linked_task_id);
			let linkedTaskEta;
			if (task.task_type_id === TaskTypeId.PICKUP) {
				linkedTaskEta = moment(updatedEtl).add(1, 'second').toDate();
			} else {
				const { eta, etl } = linkedTask?.way_points?.[0] || {};
				const taskDuration = getTaskDuration(eta, etl);
				linkedTaskEta = moment(updatedEta).subtract(taskDuration);
			}
			const isAssignedSuccess = await assignedTasksToRun([
				{ taskId: task.linked_task_id, runId, eta: linkedTaskEta },
				{ taskId, runId, eta: updatedEta }
			]);
			if (isAssignedSuccess !== false) {
				Notification.info(translations.notificationsTranslations.assignedLinkedTasks);
			}
			tasksToAssign = [task, linkedTask];
		} else {
			await assignedTasksToRun([{ taskId, runId, eta: updatedEta }]);
			tasksToAssign = [task];
		}

		assignTasksToDate(tasksToAssign, this.rootStore.tasksByDate, this.rootStore.team.time_zone);
		this.ganttApi.api.setSelection(
			tasksToAssign.map(task => task.id),
			{ focus: false, animation: {} }
		);
	};

	getRoutesAsGanttItemBySelectedRouteId = (routeId: number) => {
		const consecutiveRoutes = [];
		const routesWithoutCurrent = consecutiveRoutesProvider.getByRouteId(routeId, this.rootStore);
		const { translations } = this.rootStore;

		routesWithoutCurrent.forEach(route => {
			const id = etaRoutesConverter.getRouteId(route);
			const consecutiveRoute = this.rootStore.consecutiveRoutesStore.routesById.get(id);

			const consecutiveRouteTranslations = {
				consecutiveRoute: translations.consecutiveRoutesTranslations.consecutiveRoute,
				consecutiveRoutes: translations.consecutiveRoutesTranslations.consecutiveRoutes
			};

			if (consecutiveRoute && this.rootStore.consecutiveRoutesStore.routesTasksByRouteId[id].length > 0) {
				consecutiveRoutes.push(
					consecutiveRouteToGanttItem(consecutiveRoute, routeId, consecutiveRouteTranslations)
				);
			} else if (route && this.rootStore.runToTasks[id]?.length > 0) {
				consecutiveRoutes.push(consecutiveRouteToGanttItem(route, routeId, consecutiveRouteTranslations));
			}
		});

		return consecutiveRoutes;
	};

	onDropObjectOnItem = objectData => {
		const { tableStore } = this.rootStore;
		tableStore.onDraggableItemDropped(objectData.task_id);
	};

	setLastUpdatedRunIds = (runIds: number[]) => {
		this.lastUpdatedRunIds = runIds;
	};

	getLastUpdatedRunIds = () => {
		return this.lastUpdatedRunIds;
	};
}
